import {RequestError} from '../errors/requestError';
import {isBrowserRuntime} from '../runtime/browser';
import {type RequestOptions, RequestResponseType} from '../types/request';

import type {
  ClientCredentialsCall,
  ClientCredentialsCallResult,
  ClientCredentialsExecutor,
} from './types';

const CLIENT_CREDENTIALS_PREFIXES = [
  '/api/core-cc/',
  '/api/plutus-cc/',
  '/api/apollo-cc/',
];

// On globalThis like the guard and token cache: the app registers from its
// client instrumentation bundle, which is not the bundle that calls request().
interface ExecutorGlobal {
  __zoeClientCredentialsExecutor?: ClientCredentialsExecutor;
}

/**
 * Lets a consuming app run browser client-credentials calls through a Server
 * Action, so the token stays on the server. The app owns the action (and its
 * allowlist) so this package stays free of Next.
 */
export function registerClientCredentialsExecutor(
  executor: ClientCredentialsExecutor,
): void {
  (globalThis as ExecutorGlobal).__zoeClientCredentialsExecutor = executor;
}

/** Same-origin absolute URLs become a path; anything else is kept as given. */
const toSameOriginPath = (url: string): string => {
  if (url.startsWith('/')) {
    return url;
  }

  try {
    const parsed = new URL(url);

    return parsed.origin === window.location.origin
      ? `${parsed.pathname}${parsed.search}`
      : url;
  } catch {
    return url;
  }
};

/**
 * The call to hand to the Server Action, or `undefined` when this request
 * should run normally: not in a browser, not a client-credentials route, a
 * non-JSON response type, or no executor registered.
 */
export const getDelegatedClientCredentialsCall = (
  url: string,
  options?: RequestOptions,
): ClientCredentialsCall | undefined => {
  const isJsonResponse =
    !options?.responseType || options.responseType === RequestResponseType.JSON;

  if (!isBrowserRuntime() || !isJsonResponse) {
    return undefined;
  }

  const executor = (globalThis as ExecutorGlobal)
    .__zoeClientCredentialsExecutor;
  const path = toSameOriginPath(url);

  if (
    !executor ||
    !CLIENT_CREDENTIALS_PREFIXES.some((prefix) => path.startsWith(prefix))
  ) {
    return undefined;
  }

  return {
    method: options?.method ?? 'GET',
    path,
    ...(options?.body !== undefined && {body: options.body}),
  };
};

const throwIfAborted = (signal?: AbortSignal | null): void => {
  if (signal?.aborted) {
    throw new DOMException('The operation was aborted.', 'AbortError');
  }
};

/**
 * Runs a delegated call through the registered Server Action. A Server Action
 * can't be cancelled, so an abort is honoured before and after the call.
 */
export const executeDelegatedClientCredentialsCall = async (
  call: ClientCredentialsCall,
  signal?: AbortSignal | null,
): Promise<ClientCredentialsCallResult['data']> => {
  const executor = (globalThis as ExecutorGlobal)
    .__zoeClientCredentialsExecutor;

  throwIfAborted(signal);

  if (!executor) {
    throw new RequestError({
      message: 'No client-credentials executor registered',
      url: call.path,
      method: call.method,
    });
  }

  const result = await executor(call);

  throwIfAborted(signal);

  if (!result.ok) {
    throw new RequestError({
      message: `Request failed with status ${result.status}`,
      status: result.status,
      data: result.data,
      url: call.path,
      method: call.method,
    });
  }

  return result.data;
};
